import React from "react";
import { X, Loader2, MoreHorizontal, ExternalLink, FileText, Plus, PauseCircle, PlayCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Avatar, PlanBadge, StatusPill, DeliveryPill, PipelinePill, Pill, formatEUR, formatDate,
  formatDateTime, eventTypeLabel, PLAN_LABEL,
} from "./adminUi";

function Row({ label, children }) {
  return (
    <div className="flex gap-2 text-sm leading-6">
      <span className="text-neutral-500 shrink-0">{label}:</span>
      <span className="min-w-0 break-words">{children ?? "—"}</span>
    </div>
  );
}

function Section({ title, action, children, testid }) {
  return (
    <section className="pt-5 border-t border-border first:border-t-0 first:pt-0" data-testid={testid}>
      <div className="flex items-center justify-between gap-2 mb-2.5">
        <h3 className="label-uppercase text-neutral-700">{title}</h3>
        {action}
      </div>
      {children}
    </section>
  );
}

function EmailBlock({ rec, kind, onRetry, testid }) {
  if (!rec) return <div className="text-sm text-neutral-500">Nessuna email registrata</div>;
  const canRetry = (["failed","no_recipient"].includes(rec.status) || ["failed","bounced"].includes(rec.delivery_status))
    && !["delivered","opened","clicked","complained","delayed"].includes(rec.delivery_status);
  return (
    <div className="text-xs space-y-1.5">
      <div className="flex items-center justify-between gap-2">
        <span className="text-neutral-700 truncate">{rec.to || "—"}</span>
        <DeliveryPill status={rec.delivery_status || rec.status} />
      </div>
      <div className="text-neutral-500">Invoice <code className="text-[11px]">{rec.invoice_id}</code> · tentativi: {rec.attempts || 0} · ultimo: {formatDateTime(rec.last_attempt_at)}</div>
      {rec.last_error && <div className="text-rose-700">Errore: {rec.last_error}</div>}
      {rec.delivery_status === "bounced" && (
        <div className="text-amber-800">Verifica/correggi prima l'indirizzo destinatario.</div>
      )}
      {canRetry && (
        <Button size="sm" variant="outline" className="mt-1 rounded-full h-8" onClick={() => onRetry(kind, rec.invoice_id)} data-testid={testid}>
          Riprova invio
        </Button>
      )}
    </div>
  );
}

// v9.50 Sprint 3 drawer — kept and improved (Sprint ACC). Data source: GET /admin/users/{id}/billing-overview
export default function BillingDrawer({
  overview, loading, error, onClose, onGrantClick, onRetryEmail,
  onBonus, onToggleSuspend, isOwner, busy,
}) {
  const u = overview?.user;
  const sub = overview?.subscription;
  const bd = overview?.billing_data;
  const lp = overview?.latest_payment;
  const ae = overview?.accounting_email;
  const ce = overview?.customer_receipt_email;
  const ps = overview?.pipeline_status;
  const evts = overview?.recent_stripe_events || [];
  const payments = overview?.payments || [];
  const isManual = sub?.origin === "manual";
  const durationLabel =
    sub?.manual_grant_duration === "one_month" ? "1 mese"
    : sub?.manual_grant_duration === "one_year" ? "1 anno"
    : sub?.manual_grant_duration === "permanent" ? "Senza scadenza"
    : sub?.billing_cycle === "yearly" ? "Annuale"
    : sub?.billing_cycle === "monthly" ? "Mensile"
    : "—";
  const originLabel = sub?.origin === "stripe" ? "Stripe"
    : sub?.origin === "manual" ? (sub?.manual_grant_kind === "gift" ? "Omaggio" : "Manuale") : "—";

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="admin-billing-drawer">
      <div className="flex-1 bg-black/30" onClick={onClose} />
      <aside className="w-full sm:w-[520px] bg-white shadow-2xl overflow-y-auto">
        <div className="sticky top-0 bg-white/95 backdrop-blur border-b border-border px-5 py-4 z-10">
          <div className="flex items-center justify-between">
            <div className="label-uppercase text-neutral-500">Account</div>
            <button className="p-1.5 rounded-full hover:bg-neutral-100" onClick={onClose} data-testid="drawer-close">
              <X className="w-4 h-4" strokeWidth={2} />
            </button>
          </div>
          {u && (
            <div className="mt-3 flex items-center gap-3">
              <Avatar name={u.name} email={u.email} size="lg" />
              <div className="min-w-0 flex-1">
                <div className="font-serif-heading text-xl truncate">{u.name || "—"}</div>
                <div className="text-sm text-neutral-500 truncate">{u.email}</div>
              </div>
              <StatusPill status={sub?.billing_status} suspended={u.suspended} cancelAtPeriodEnd={sub?.cancel_at_period_end} periodEnd={sub?.current_period_end} testid="drawer-status-pill" />
            </div>
          )}
        </div>

        {loading && (
          <div className="p-8 flex items-center justify-center">
            <Loader2 className="w-5 h-5 animate-spin text-primary" strokeWidth={1.5} />
          </div>
        )}
        {error && !loading && (
          <div className="p-6 text-sm text-rose-700 bg-rose-50 m-4 rounded-md border border-rose-100">
            Errore nel caricamento: {String(error)}
          </div>
        )}

        {!loading && overview && (
          <div className="p-5 space-y-5">
            <Section title="Account">
              <Row label="Nome">{u?.name || "—"}</Row>
              <Row label="Email">{u?.email || "—"}</Row>
              <Row label="Telefono">{u?.phone || "—"}</Row>
              <Row label="Registrato">{formatDate(u?.created_at)}</Row>
              <Row label="Ultimo accesso">{formatDate(u?.last_login)}</Row>
              {u?.suspended && <div className="text-rose-700 text-sm font-medium">Account sospeso</div>}
              {u?.is_admin_owner && <div className="text-primary text-sm font-medium">Proprietario</div>}
            </Section>

            <Section title="Abbonamento">
              <Row label="Piano"><PlanBadge plan={sub?.plan} /></Row>
              <Row label="Origine">{originLabel}</Row>
              <Row label="Ciclo/Durata">{durationLabel}</Row>
              <Row label="Stato"><StatusPill status={sub?.billing_status} /></Row>
              <Row label="Scadenza/Rinnovo">
                {sub?.manual_grant_duration === "permanent" ? (
                  <span className="text-neutral-700 font-medium">Senza scadenza</span>
                ) : sub?.current_period_end ? (
                  <>{formatDate(sub.current_period_end)} {sub?.origin === "stripe" && sub?.billing_status === "active" && !sub?.cancel_at_period_end && <span className="text-neutral-500">(si rinnova)</span>}</>
                ) : "—"}
              </Row>
              {sub?.cancel_at_period_end && (
                <div className="text-amber-800 text-xs mt-1">Cancellazione a fine periodo{sub?.canceled_at ? ` · richiesta il ${formatDate(sub.canceled_at)}` : ""}</div>
              )}
              {isManual && (
                <>
                  <Row label="Motivo">{sub?.manual_grant_reason || "—"}</Row>
                  <Row label="Concesso da">{sub?.manual_granted_by || "—"}</Row>
                </>
              )}
              {(sub?.stripe_customer_id || sub?.stripe_subscription_id) && (
                <div className="pt-1 text-xs space-y-0.5">
                  {sub?.stripe_customer_id && <div><span className="text-neutral-500">Customer ID:</span> <code className="text-[11px]">{sub.stripe_customer_id}</code></div>}
                  {sub?.stripe_subscription_id && <div><span className="text-neutral-500">Subscription ID:</span> <code className="text-[11px]">{sub.stripe_subscription_id}</code></div>}
                </div>
              )}
              <div className="pt-3 flex items-center gap-2">
                <Button className="rounded-full" onClick={onGrantClick} data-testid="drawer-open-grant">
                  Assegna / modifica piano
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button type="button" className="h-10 w-10 inline-flex items-center justify-center rounded-md border border-border hover:bg-neutral-50" data-testid="drawer-more-actions" disabled={!!busy}>
                      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <MoreHorizontal className="w-4 h-4" strokeWidth={1.8} />}
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="min-w-[200px]">
                    <DropdownMenuItem onClick={() => onBonus?.(u.id)} data-testid="drawer-action-bonus">
                      <Plus className="w-4 h-4 mr-2" /> +10 contratti bonus
                    </DropdownMenuItem>
                    {!isOwner && (
                      <DropdownMenuItem onClick={() => onToggleSuspend?.(u.id, !!u.suspended)} data-testid="drawer-action-suspend">
                        {u?.suspended ? <PlayCircle className="w-4 h-4 mr-2" /> : <PauseCircle className="w-4 h-4 mr-2" />}
                        {u?.suspended ? "Riattiva account" : "Sospendi account"}
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </Section>

            <Section title="Ultimo pagamento" action={lp?.hosted_invoice_url ? (
              <a href={lp.hosted_invoice_url} target="_blank" rel="noreferrer" className="text-xs inline-flex items-center gap-1 border border-border rounded-md px-2.5 py-1.5 hover:bg-neutral-50" data-testid="drawer-latest-invoice-link">
                <FileText className="w-3.5 h-3.5" /> Vedi ricevuta
              </a>
            ) : null}>
              {isManual ? (
                <div className="text-sm text-neutral-600">Omaggio / assegnazione manuale · Nessun pagamento richiesto</div>
              ) : lp ? (
                <div className="text-sm space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="font-serif-heading text-2xl tabular-nums">{formatEUR(lp.amount || 0)}</span>
                    {["paid","complete","succeeded"].includes(lp.payment_status)
                      ? <Pill tone="ok" dot>Pagamento riuscito</Pill>
                      : <Pill tone="fail" dot>Fallito</Pill>}
                  </div>
                  <div className="text-neutral-500 text-xs">{formatDate(lp.created_at)}{lp.billing_reason ? ` · ${eventTypeLabel({ billing_reason: lp.billing_reason, status: lp.payment_status })}` : ""}</div>
                  {lp.stripe_invoice_number && <Row label="Invoice">{lp.stripe_invoice_number}</Row>}
                  {lp.stripe_invoice_id && !lp.stripe_invoice_number && <Row label="Invoice"><code className="text-[11px]">{lp.stripe_invoice_id}</code></Row>}
                </div>
              ) : (
                <div className="text-sm text-neutral-500">Nessun pagamento registrato</div>
              )}
            </Section>

            {payments.length > 0 && !isManual && (
              <Section title={`Storico pagamenti (${payments.length})`}>
                <div className="text-xs divide-y divide-border">
                  {payments.map((tx, i) => (
                    <div key={i} className="py-2 flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className="tabular-nums">{formatDate(tx.created_at)} <span className="text-neutral-500">· {eventTypeLabel({ billing_reason: tx.billing_reason, status: tx.payment_status })}</span></div>
                        <div className="text-neutral-500 text-[11px] truncate">
                          {PLAN_LABEL[tx.metadata?.plan || tx.plan] || "—"} · {(tx.metadata?.cycle || tx.cycle) === "yearly" ? "Annuale" : (tx.metadata?.cycle || tx.cycle) === "monthly" ? "Mensile" : "—"}
                          {tx.stripe_invoice_number ? ` · ${tx.stripe_invoice_number}` : ""}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <div className="text-right">
                          <div className="tabular-nums font-medium">{formatEUR(tx.amount || 0)}</div>
                          <div className={`text-[11px] ${["paid","complete","succeeded"].includes(tx.payment_status) ? "text-emerald-700" : "text-rose-700"}`}>
                            {["paid","complete","succeeded"].includes(tx.payment_status) ? "Pagato" : "Fallito"}
                          </div>
                        </div>
                        {tx.hosted_invoice_url && (
                          <a href={tx.hosted_invoice_url} target="_blank" rel="noreferrer" className="p-1.5 rounded-md border border-border hover:bg-neutral-50" title="Vedi fattura Stripe">
                            <ExternalLink className="w-3.5 h-3.5" />
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </Section>
            )}

            <Section title="Dati di fatturazione" action={bd?.complete
              ? <Pill tone="ok">Completi</Pill>
              : <Pill tone="warn">Incompleti</Pill>}>
              <Row label="Tipo">{bd?.type || "—"}</Row>
              <Row label="Nome">{bd?.business_name || `${bd?.first_name || ""} ${bd?.last_name || ""}`.trim() || "—"}</Row>
              <Row label="P.IVA">{bd?.vat_number || "—"}</Row>
              <Row label="Codice fiscale">{bd?.tax_code || "—"}</Row>
              <Row label="Indirizzo">{bd?.address ? `${bd.address}, ${bd.zip || ""} ${bd.city || ""} ${bd.province ? `(${bd.province})` : ""}`.replace(/\s+/g, " ").trim() : "—"}</Row>
              <Row label="SDI"><code className="text-[11px]">{bd?.sdi_code || "—"}</code></Row>
              <Row label="PEC"><code className="text-[11px]">{bd?.pec_email || "—"}</code></Row>
            </Section>

            <Section title="Stato pipeline">
              <div className="grid grid-cols-2 gap-2">
                <PipelinePill label="Pagamento Stripe" kind={ps?.payment} />
                <PipelinePill label="Webhook" kind={ps?.webhook} />
                <PipelinePill label="Account aggiornato" kind={ps?.account} />
                <PipelinePill label="Dati fiscali" kind={ps?.billing_data} />
                <PipelinePill label="Email fatturazione" kind={ps?.accounting_email} />
                <PipelinePill label="Email cliente" kind={ps?.customer_receipt_email} />
              </div>
            </Section>

            <Section title="Email amministrativa">
              <EmailBlock rec={ae} kind="accounting" onRetry={onRetryEmail} testid="drawer-retry-accounting" />
            </Section>

            <Section title="Ricevuta cliente">
              <EmailBlock rec={ce} kind="customer_receipt" onRetry={onRetryEmail} testid="drawer-retry-receipt" />
            </Section>

            <Section title="Eventi Stripe recenti">
              {evts.length === 0 ? (
                <div className="text-sm text-neutral-500">Nessun evento associato</div>
              ) : (
                <div className="text-xs divide-y divide-border">
                  {evts.map((e, i) => (
                    <div key={i} className="py-2">
                      <div className="flex items-center gap-2">
                        <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${e.status === "processed" ? "bg-emerald-500" : e.status === "failed" ? "bg-rose-500" : "bg-amber-500"}`} />
                        <span className="font-medium flex-1 truncate">{e.event_type}</span>
                        <span className="text-neutral-500 tabular-nums">{formatDateTime(e.received_at)}</span>
                      </div>
                      <div className="text-[11px] text-neutral-500 pl-3.5">tentativi: {e.attempt_count || 0} · {e.status}</div>
                      {e.status === "failed" && (
                        <div className="text-[11px] text-rose-700 pl-3.5">Errore webhook — attesa retry Stripe / verifica operatore</div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </Section>
          </div>
        )}
      </aside>
    </div>
  );
}
